import type { Theme } from "@mui/material/styles";

export const COLUMN_WIDTHS_MM: Record<string, number> = {
  programAdmin: 30,
  careManager: 30,
  reportTypes: 26,
};

export const FLEXIBLE_MIN_WIDTHS_MM: Record<string, number> = {
  program: 38,
  member: 26,
  description: 42,
};

export const PDF_HEADER_OVERRIDES: Record<string, string> = {
  "Participant/Member": "Participant/\nMember",
};

// Normalizes any valid CSS color (#hex, rgb(), rgba(), named colors, etc.) to an [r, g, b] tuple for jsPDF
export const cssColorToRgb = (color: string): [number, number, number] => {
  const ctx = document.createElement("canvas").getContext("2d");
  if (!ctx) return [0, 0, 0];

  ctx.fillStyle = color;
  const normalized = ctx.fillStyle; // browser resolves to "#rrggbb" or "rgba(...)"

  if (normalized.startsWith("#")) {
    const clean = normalized.slice(1);
    return [parseInt(clean.substring(0, 2), 16), parseInt(clean.substring(2, 4), 16), parseInt(clean.substring(4, 6), 16)];
  }

  const match = normalized.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (match) return [Number(match[1]), Number(match[2]), Number(match[3])];

  return [0, 0, 0];
};

// Resolves an sx-style color token ("success.main", "custom.main", "default") against the theme
export const resolveThemeColor = (theme: Theme, token?: string): string => {
  if (!token || token === "default") return theme.palette.text.primary;
  const [group, key] = token.split(".");
  return (theme.palette as any)[group]?.[key] ?? theme.palette.text.primary;
};

// Material icon path data (24x24 viewBox) for the icons used in Trend/Risk columns
const ICON_PATHS: Record<string, string> = {
  trendingUp: "M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z",
  trendingDown: "M16 18l2.29-2.29-4.88-4.88-4 4L2 7.41 3.41 6l6 6 4-4 6.3 6.29L22 12v6z",
  trendingFlat: "M22 12l-4-4v3H3v2h15v3z",
  warning: "M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z",
  error: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z",
  report:
    "M15.73 3H8.27L3 8.27v7.46L8.27 21h7.46L21 15.73V8.27L15.73 3zM12 17.3c-.72 0-1.3-.58-1.3-1.3 0-.72.58-1.3 1.3-1.3.72 0 1.3.58 1.3 1.3 0 .72-.58 1.3-1.3 1.3zm1-4.3h-2V7h2v6z",
};

const measureCanvas = document.createElement("canvas");
const measureCtx = measureCanvas.getContext("2d")!;

// Renders white-fill/black-stroke text (used for the snapshot bar overlay, which sits on
// arbitrary background colors) via canvas — crisper than PDF's native text stroke at small sizes
export const renderOutlinedLabelDataUrl = (text: string, fontSize: number): { dataUrl: string; width: number; height: number } => {
  const scale = 4;
  const fontSpec = `bold ${fontSize * scale}px Helvetica, Arial, sans-serif`;

  measureCtx.font = fontSpec;
  const textWidth = measureCtx.measureText(text).width;

  const lineWidthPx = 1.2 * scale;
  measureCanvas.width = textWidth + lineWidthPx * 2;
  measureCanvas.height = fontSize * scale * 1.6;

  // resizing the canvas resets its context state, so font must be reapplied
  measureCtx.font = fontSpec;
  measureCtx.textBaseline = "middle";
  measureCtx.textAlign = "left";
  measureCtx.lineJoin = "round";

  const drawX = lineWidthPx;
  const drawY = measureCanvas.height / 2;

  measureCtx.strokeStyle = "#000000";
  measureCtx.lineWidth = lineWidthPx;
  measureCtx.strokeText(text, drawX, drawY);

  measureCtx.fillStyle = "#ffffff";
  measureCtx.fillText(text, drawX, drawY);

  return { dataUrl: measureCanvas.toDataURL("image/png"), width: measureCanvas.width, height: measureCanvas.height };
};

const iconTextCanvas = document.createElement("canvas");
const iconTextCtx = iconTextCanvas.getContext("2d")!;

// Renders an optional icon glyph + colored text on a plain (non-bar) background — used for
// Trend, Risk Level, and Variance columns so the PDF keeps the same color/icon cues as the grid
export const renderIconTextDataUrl = (
  text: string,
  color: string,
  iconKey: string | null | undefined,
  fontSize = 8,
): { dataUrl: string; width: number; height: number } => {
  const scale = 4;
  const fontSpec = `${fontSize * scale}px Helvetica, Arial, sans-serif`;
  const iconSize = fontSize * scale * 1.4;
  const gap = iconKey ? 3 * scale : 0;

  iconTextCtx.font = fontSpec;
  const textWidth = iconTextCtx.measureText(text).width;

  iconTextCanvas.width = (iconKey ? iconSize : 0) + gap + textWidth + 4 * scale;
  iconTextCanvas.height = fontSize * scale * 1.8;

  // resizing the canvas resets its context state, so font must be reapplied
  iconTextCtx.font = fontSpec;
  iconTextCtx.textBaseline = "middle";
  iconTextCtx.textAlign = "left";
  iconTextCtx.fillStyle = color;

  const centerY = iconTextCanvas.height / 2;
  let cursorX = 0;

  if (iconKey && ICON_PATHS[iconKey]) {
    const path = new Path2D(ICON_PATHS[iconKey]);
    iconTextCtx.save();
    iconTextCtx.translate(0, centerY - iconSize / 2);
    iconTextCtx.scale(iconSize / 24, iconSize / 24);
    iconTextCtx.fill(path);
    iconTextCtx.restore();
    cursorX = iconSize + gap;
  }

  iconTextCtx.fillText(text, cursorX, centerY);

  return { dataUrl: iconTextCanvas.toDataURL("image/png"), width: iconTextCanvas.width, height: iconTextCanvas.height };
};
